import { useState, useEffect } from "react";
import { axiosInstance, API_ENDPOINTS } from "config/api";

/**
 * Custom hook for managing patient data and related operations
 * Handles fetching patients based on user role, filtering, and patient selection
 */
export const usePatientData = (
  isDoctor,
  isOwner,
  isAdmin,
  isFrontdesk,
  currentDoctorId,
  clinicId
) => {
  const [patientsData, setPatientsData] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch patients based on user role
  useEffect(() => {
    console.log('🔥🔥🔥 === PATIENT LIST USEEFFECT START ===');
    console.log('🔥 useEffect triggered for patient fetching');
    console.log('🔥 Role detection:', {
      isDoctor,
      isOwner,
      isAdmin,
      isFrontdesk,
      currentDoctorId,
      clinicId
    });

    const fetchPatients = async () => {
      try {
        console.log('🚀 Starting fetchPatients function');
        setIsLoading(true);

        let endpoint;
        let endpointReason;

        if (isAdmin) {
          // Admin: fetch all patients (default endpoint)
          endpoint = API_ENDPOINTS.PATIENT.LIST;
          endpointReason = "Admin - fetching all patients";
        } else if ((isOwner || isFrontdesk) && clinicId) {
          // Owner doctors and frontdesk: fetch clinic patients first, fallback to doctor_id if empty
          endpoint = `${API_ENDPOINTS.PATIENT.LIST}?clinic_id=${clinicId}`;
          endpointReason = `${isOwner ? 'Owner' : 'Frontdesk'} - fetching clinic patients`;
        } else if (isDoctor && !isOwner && currentDoctorId) {
          // Non-owner doctors: fetch only their patients
          endpoint = `${API_ENDPOINTS.PATIENT.LIST}?doctor_id=${currentDoctorId}`;
          endpointReason = "Regular doctor - fetching own patients";
        } else {
          console.log('❌ No valid endpoint condition met');
          console.log('❌ Conditions check:', {
            isAdmin,
            isOwnerWithClinic: isOwner && clinicId,
            isFrontdeskWithClinic: isFrontdesk && clinicId,
            isDoctorWithId: isDoctor && !isOwner && currentDoctorId
          });
          setIsLoading(false);
          return;
        }

        console.log('🔍 === PATIENT FETCH API CALL START ===');
        console.log('🔍 Fetching patients from endpoint:', endpoint);
        console.log('🔍 Endpoint reason:', endpointReason);
        console.log('🔍 User role info:', { isOwner, isAdmin, isDoctor, isFrontdesk, currentDoctorId, clinicId });
        console.log('🔍 About to call axiosInstance.get with endpoint:', endpoint);

        let response = await axiosInstance.get(endpoint);

        console.log('🔍 === PATIENT FETCH API RESPONSE ===');
        console.log('🔍 Raw API response:', response);
        console.log('🔍 Response status:', response.status);
        console.log('🔍 Response statusText:', response.statusText);
        console.log('🔍 Response headers:', response.headers);
        console.log('🔍 Response data:', response.data);
        console.log('🔍 Response data.data:', response.data?.data);
        console.log('🔍 Is response.data.data an array?', Array.isArray(response.data?.data));
        console.log('🔍 Response data.data length:', response.data?.data?.length);

        // Check if clinic_id returned empty results and we have a fallback option
        if ((isOwner || isFrontdesk) && clinicId && currentDoctorId &&
            response.data && Array.isArray(response.data.data) &&
            response.data.data.length === 0) {

          console.log('🔄 === FALLBACK TO DOCTOR_ID ===');
          console.log('🔄 Clinic endpoint returned empty, trying doctor_id fallback');

          const fallbackEndpoint = `${API_ENDPOINTS.PATIENT.LIST}?doctor_id=${currentDoctorId}`;
          console.log('🔄 Fallback endpoint:', fallbackEndpoint);

          response = await axiosInstance.get(fallbackEndpoint);

          console.log('🔄 === FALLBACK API RESPONSE ===');
          console.log('🔄 Fallback response:', response);
          console.log('🔄 Fallback response data:', response.data);
          console.log('🔄 Fallback response data.data:', response.data?.data);
          console.log('🔄 Fallback response data.data length:', response.data?.data?.length);
        }

        if (response.data && Array.isArray(response.data.data)) {
          console.log('🔍 Number of patients received:', response.data.data.length);
          console.log('🔍 First patient sample:', response.data.data[0]);

          // Map API fields to table fields - handleBookAppointment will be set by parent
          const mappedPatients = response.data.data.map(patient => ({
            ...patient,
            id: patient.patient_id,
            phoneNumber: patient.phone_no
          }));

          console.log('🔍 Mapped patients:', mappedPatients);
          console.log('🔍 First mapped patient sample:', mappedPatients[0]);
          setPatientsData(mappedPatients);
        } else {
          console.log('❌ Invalid response structure or empty data array');
          console.log('❌ Response structure:', {
            hasData: !!response.data,
            dataType: typeof response.data,
            hasDataArray: !!response.data?.data,
            dataArrayType: typeof response.data?.data,
            isArray: Array.isArray(response.data?.data)
          });
          console.error('Invalid data format received:', response.data);
          setPatientsData([]);
        }
      } catch (error) {
        console.log('🔥🔥🔥 === PATIENT FETCH ERROR ===');
        console.error('🔥 Error fetching patients:', error);
        console.error('🔥 Error message:', error.message);
        console.error('🔥 Error response:', error.response);
        console.error('🔥 Error config:', error.config);
        console.log('🔥🔥🔥 === END PATIENT FETCH ERROR ===');
        setPatientsData([]);
      } finally {
        setIsLoading(false);
        console.log('🔥 Patient fetch completed, isLoading set to false');
      }
    };

    console.log('🔥 About to call fetchPatients()');
    fetchPatients();
    console.log('🔥🔥🔥 === PATIENT LIST USEEFFECT END ===');
  }, [isDoctor, isOwner, isAdmin, currentDoctorId, clinicId]);

  // Filter patients by name and mobile number
  const filterPatients = (patients, searchQuery) => {
    if (!searchQuery.trim()) {
      return patients;
    }

    const query = searchQuery.toLowerCase().trim();
    return patients.filter(patient => {
      const nameMatch = patient.name?.toLowerCase().includes(query);
      const mobileMatch = patient.phoneNumber?.toString().includes(query);
      return nameMatch || mobileMatch;
    });
  };

  // Handle patient row click
  const handleRowClick = async (patient) => {
    console.log("Clicked patient data:", patient);
    console.log("Appointment ID:", patient.appointment_id);
    // setSelectedPatient(patient);

    /* try {
      const response = await axiosInstance.post('/api/appointment/updateApptStatus', {
        id: patient.appointment_id,
        status: 'IN_PROGRESS'
      });

      console.log('Status update API response:', response.data);
    } catch (error) {
      console.error('Error updating appointment status:', error);
    } */
  };

  // Refresh patients function for external use
  const refreshPatients = async () => {
    try {
      let endpoint;
      let endpointReason;

      if (isAdmin) {
        // Admin: fetch all patients (default endpoint)
        endpoint = API_ENDPOINTS.PATIENT.LIST;
        endpointReason = "Admin - fetching all patients";
      } else if ((isOwner || isFrontdesk) && clinicId) {
        // Owner doctors and frontdesk: fetch clinic patients first, fallback to doctor_id if empty
        endpoint = `${API_ENDPOINTS.PATIENT.LIST}?clinic_id=${clinicId}`;
        endpointReason = `${isOwner ? 'Owner' : 'Frontdesk'} - fetching clinic patients`;
      } else if (isDoctor && !isOwner && currentDoctorId) {
        // Non-owner doctors: fetch only their patients
        endpoint = `${API_ENDPOINTS.PATIENT.LIST}?doctor_id=${currentDoctorId}`;
        endpointReason = "Regular doctor - fetching own patients";
      } else {
        console.log('🔄 Refresh - No valid endpoint condition met');
        return;
      }

      console.log('🔄 Refreshing patients from endpoint:', endpoint);
      console.log('🔄 Refresh endpoint reason:', endpointReason);
      let response = await axiosInstance.get(endpoint);
      console.log('🔄 Refresh API response:', response.data);

      // Check if clinic_id returned empty results and we have a fallback option
      if ((isOwner || isFrontdesk) && clinicId && currentDoctorId &&
          response.data && Array.isArray(response.data.data) &&
          response.data.data.length === 0) {

        console.log('🔄 Refresh - Clinic endpoint returned empty, trying doctor_id fallback');
        const fallbackEndpoint = `${API_ENDPOINTS.PATIENT.LIST}?doctor_id=${currentDoctorId}`;
        console.log('🔄 Refresh - Fallback endpoint:', fallbackEndpoint);
        response = await axiosInstance.get(fallbackEndpoint);
        console.log('🔄 Refresh - Fallback response:', response.data);
      }

      if (response.data && Array.isArray(response.data.data)) {
        console.log('🔄 Refresh - Number of patients:', response.data.data.length);
        const mappedPatients = response.data.data.map(patient => ({
          ...patient,
          id: patient.patient_id,
          phoneNumber: patient.phone_no
        }));
        console.log('🔄 Refresh - Mapped patients:', mappedPatients);
        setPatientsData(mappedPatients);
      } else {
        console.log('🔄 Refresh - Invalid response structure:', response.data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  return {
    // State
    patientsData,
    setPatientsData,
    selectedPatient,
    setSelectedPatient,
    isLoading,

    // Actions
    filterPatients,
    handleRowClick,
    refreshPatients
  };
};